import React, { memo } from 'react';
import { Table } from 'antd';
import type { TableProps, TableColumnType, PaginationProps } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';
import { COLORS, FONT_WEIGHTS } from '../../utils/constants';
import Spinner from './Spinner';

// Global styles for table rows - scoped to avoid conflicts
const TableGlobalStyles = createGlobalStyle<{ tableId: string }>`
  .custom-table-${(props) => props.tableId} {
    .even-row {
      background-color: ${COLORS.grey.light} !important;
    }
    .odd-row {
      background-color: ${COLORS.white.primary} !important;
    }
  }
`;

const StyledTableWrapper = styled.div<{ tableId: string }>`
  .ant-table-thead > tr > th {
    padding: 16px;
    background-color: ${COLORS.blue.primary} !important;
    color: ${COLORS.white.primary} !important;
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    border-bottom: none !important;
  }

  .ant-table-tbody > tr > td {
    padding: 16px;
  }
`;

export const StyledLoadingWrapper = styled.div`
  display: flex !important;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

// Props interface extending Ant Design's TableProps
interface CustomTableProps<T extends object>
  extends Omit<
    TableProps<T>,
    'rowClassName' | 'onRow' | 'pagination' | 'loading'
  > {
  dataSource: T[];
  columns: TableColumnType<T>[];
  rowKey: keyof T | ((record: T) => React.Key);
  loading?: boolean;
  pagination?: false | PaginationProps;
  onRowClick?: (record: T, index: number) => void;
  enableZebraStripes?: boolean;
  enableRowHover?: boolean;
  tableId?: string; // For CSS scoping
  enableInnerScroll?: boolean;
  scrollHeight?: number | string;
}

// Generate unique table ID for CSS scoping
let tableCounter = 0;
const generateTableId = () => `table-${++tableCounter}`;

function CustomTableComponent<T extends object>({
  dataSource,
  columns,
  rowKey,
  loading = false,
  pagination = { pageSize: 10 },
  onRowClick,
  enableZebraStripes = true,
  enableRowHover = true,
  tableId = generateTableId(),
  enableInnerScroll = false,
  scrollHeight = 400,
  ...restProps
}: CustomTableProps<T>) {
  // Memoize handlers to prevent unnecessary re-renders
  const rowClassName = React.useCallback(
    (record: T, index: number) => {
      if (!enableZebraStripes) return '';
      return index % 2 === 0 ? 'even-row' : 'odd-row';
    },
    [enableZebraStripes],
  );

  const onRow = React.useCallback(
    (record: T, index?: number) => {
      if (!onRowClick || index === undefined) return {};

      return {
        onClick: () => onRowClick(record, index),
        style: { cursor: 'pointer' },
      };
    },
    [onRowClick],
  );

  // Memoize table props to prevent unnecessary re-renders
  const tableProps = React.useMemo(
    () => ({
      dataSource: dataSource,
      columns,
      rowKey,
      loading,
      pagination,
      rowClassName: enableZebraStripes ? rowClassName : undefined,
      onRow: onRowClick ? onRow : undefined,
      scroll: enableInnerScroll ? { y: scrollHeight } : undefined,
      ...restProps,
    }),
    [
      dataSource,
      columns,
      rowKey,
      loading,
      pagination,
      enableZebraStripes,
      rowClassName,
      onRowClick,
      onRow,
      restProps,
      enableInnerScroll,
      scrollHeight,
    ],
  );

  return (
    <>
      <TableGlobalStyles tableId={tableId} />
      <StyledTableWrapper
        tableId={tableId}
        className={`custom-table-${tableId}`}
      >
        <Table<T>
          {...tableProps}
          loading={{
            spinning: loading,
            indicator: (
              <StyledLoadingWrapper>
                <Spinner />
              </StyledLoadingWrapper>
            ),
          }}
          {...(loading ? { locale: { emptyText: null } } : {})}
        />
      </StyledTableWrapper>
    </>
  );
}

// Export memoized component to prevent unnecessary re-renders
export const CustomTable = memo(CustomTableComponent) as <T extends object>(
  props: CustomTableProps<T>,
) => React.ReactElement;

// Export types for consumers
export type { CustomTableProps };
